"use client";

import { Logo } from "@/components/layout";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Icon } from "@iconify/react";
import { useRouter } from "next/navigation";
import { signOut } from "next-auth/react";
import AuthButton from "./Button";
import { useTranslate } from "@tolgee/react";

const DB_SYNC_BANNER_KEY = "waas-auth-db-sync-banner";

export default function LoginPage({
  callbackUrl,
  authDbSyncFailed = false,
}: {
  callbackUrl?: string;
  /** Set when OAuth succeeded but DB user sync failed — clears any session then strips query. */
  authDbSyncFailed?: boolean;
}) {
  const { t } = useTranslate();
  const router = useRouter();
  const [showDbSyncBanner] = useState(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(DB_SYNC_BANNER_KEY) === "1") {
      sessionStorage.removeItem(DB_SYNC_BANNER_KEY);
      return true;
    }
    return authDbSyncFailed;
  });

  useEffect(() => {
    if (!authDbSyncFailed) return;
    sessionStorage.setItem(DB_SYNC_BANNER_KEY, "1");
    let cancelled = false;
    void (async () => {
      await signOut({ redirect: false });
      if (!cancelled) router.replace("/");
    })();
    return () => {
      cancelled = true;
    };
  }, [authDbSyncFailed, router]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/img/login-background.jpg')",
        }}
      />
      <div className="absolute inset-0 bg-black/40" />

      {/* Header */}
      <motion.div
        className="relative z-10 p-6"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Logo white={true} />
      </motion.div>

      {/* Main content */}
      <div className="relative z-10 flex h-[calc(100%-5rem)] flex-col items-center justify-center px-4">
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-8">
          {showDbSyncBanner && (
            <motion.div
              role="alert"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="w-full rounded-xl border border-amber-400/35 bg-amber-950/50 px-4 py-3 text-sm text-amber-50 shadow-lg backdrop-blur-sm"
            >
              <div className="flex gap-3">
                <Icon
                  icon="ph:warning-circle"
                  className="size-5 shrink-0 text-amber-300"
                  aria-hidden
                />
                <p className="text-left leading-relaxed text-amber-50/95">
                  {t("auth.couldnotfinishsignin")}
                </p>
              </div>
            </motion.div>
          )}

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col items-center gap-2 text-center"
          >
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              {t("weather.weatherintelligence")}
            </h1>
            <p className="max-w-xs text-sm leading-relaxed text-white/70">
              {t("auth.marketingparagraph")}
            </p>
          </motion.div>

          {/* Login card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="flex w-full flex-col gap-5 rounded-2xl border border-white/10 bg-white/10 p-6 shadow-lg backdrop-blur-md"
          >
            <div className="text-center">
              <h2 className="text-sm font-medium text-white">{t("auth.signinaccount")}</h2>
              <p className="mt-1 text-xs text-white/50">{t("auth.usecredentials")}</p>
            </div>

            <AuthButton callbackUrl={callbackUrl} />

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] font-medium tracking-wider text-white/30 uppercase">
                {t("auth.securedaccess")}
              </span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <div className="flex items-center justify-center gap-1.5 text-xs text-white/40">
              <Icon icon="ph:shield-check" className="size-3.5" />
              <span>{t("auth.enterprisesso")}</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Slogan — lower right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="absolute right-8 bottom-6 z-10 text-xl font-semibold tracking-tight text-white"
      >
        {t("auth.guidingyou")}
        <br />
        {t("auth.tothedecision")}
        <div className="ml-1 inline-block translate-y-[3px] scale-[0.8]">
          <Logo iconOnly={true} />
        </div>
      </motion.div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="pointer-events-none absolute bottom-6 left-0 z-10 w-full text-center"
      >
        <p className="text-[11px] text-white/30">
          &copy; {new Date().getFullYear()} Infoplaza. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}
